import asyncSeries from 'async/series';
import asyncParallel from 'async/parallel';
import React from 'react';
import EventEmitter from 'events';

import Api from '../tools/api';
import Storage from '../tools/storage';
import util from '../tools/util';

export default {
  once,
  addChangeListener,
  removeChangeListener,
  init,
  isLoggedIn,
  isReady,
  isAdmin,
  isSuperAdmin,
  getOrg,
  getCustomDomain,
  getUser,
  getCurrentOrgId,
  getIsAdmin,
  useIsLoggedIn,
  useIsAdmin,
  useUser,
  useOrg,
  useIsReady,
  setCurrentOrgId,
  verifySessionKey,
  emailCode,
  loginCode,
  logout,
  updateUser,
  get,
  post,
  del,
};

const SESSION_KEY = 'ARES_SESSION_KEY';
const ORG_ID_KEY = 'ARES_ORG_ID';
const CUSTOM_REGEX = /^([^.]*)\.local\.ares-dev\.com$|^([^.]*)\.maxlo\.cloud$/i;

let g_isReady = false;
let g_sessionKey: string | null = null;
let g_user: any = null;
let g_currentOrgId: any = null;
let g_customOrgId: any = null;
const HOST_MATCH = window.location.hostname.match(CUSTOM_REGEX);
const g_customDomain = HOST_MATCH?.[1] ?? HOST_MATCH?.[2] ?? null;

const g_eventEmitter = new EventEmitter();
g_eventEmitter.setMaxListeners(20);
const CHANGE_EVENT = 'change';
function once(callback) {
  g_eventEmitter.once(CHANGE_EVENT, callback);
}
function addChangeListener(callback) {
  g_eventEmitter.on(CHANGE_EVENT, callback);
}
function removeChangeListener(callback) {
  g_eventEmitter.removeListener(CHANGE_EVENT, callback);
}
function _emit(...args) {
  g_eventEmitter.emit(CHANGE_EVENT, ...args);
}
function isReady() {
  return g_isReady;
}
function isLoggedIn() {
  return Boolean(g_user);
}
function isAdmin() {
  return Boolean(g_user && g_user.is_admin);
}
function isSuperAdmin() {
  return Boolean(g_user && g_user.is_admin);
}
export function getCustomDomain() {
  return g_customDomain;
}
function getUser() {
  return g_user;
}
function getCurrentOrgId() {
  return g_currentOrgId;
}
function getOrg() {
  return g_user?.org_list?.find?.((org) => org.org_id === g_currentOrgId);
}
function getIsAdmin() {
  const org = getOrg();
  return org?.is_admin;
}
function _subscribe(callback: any) {
  addChangeListener(callback);
  return () => {
    removeChangeListener(callback);
  };
}
export function useUser() {
  return React.useSyncExternalStore(_subscribe, getUser);
}
export function useIsAdmin() {
  return React.useSyncExternalStore(_subscribe, getIsAdmin);
}
export function useIsLoggedIn() {
  return React.useSyncExternalStore(_subscribe, isLoggedIn);
}
export function useOrg() {
  return React.useSyncExternalStore(_subscribe, getOrg);
}
export function useIsReady() {
  return React.useSyncExternalStore(_subscribe, isReady);
}
function setCurrentOrgId(org_id) {
  g_currentOrgId = parseInt(org_id);
  _save();
  _emit('org_change');
}
function _findOrg(user, org_id) {
  return user?.org_list?.find?.((o) => o.org_id === org_id);
}
function _getDefaultOrgId(user) {
  let ret;
  if (g_customOrgId) {
    ret = _findOrg(user, g_customOrgId)?.org_id;
  } else if (g_customDomain) {
    const found = user?.org_list?.find?.(
      (o) => o.domain_prefix === g_customDomain
    );
    ret = g_customOrgId = found?.org_id;
  } else {
    ret = _findOrg(user, g_currentOrgId)?.org_id;
    if (!ret) {
      ret = user?.org_list?.[0]?.org_id;
    }
  }
  return ret;
}
function init(done) {
  asyncSeries(
    [
      _load,
      (done) => {
        if (g_sessionKey) {
          verifySessionKey({}, () => done());
        } else {
          done();
        }
      },
    ],
    (err) => {
      done(err);
    }
  );
}
function verifySessionKey(params, done) {
  if (g_sessionKey) {
    const opts: any = {
      url: '/api/1/user/current',
    };
    if (params?.retry) {
      opts.retry = true;
      opts.retry_times = 9999;
    }
    get(opts, (err, body) => {
      g_currentOrgId = _getDefaultOrgId(body) ?? null;
      if (err === 403 || (!g_currentOrgId && g_customDomain)) {
        g_user = null;
        g_currentOrgId = null;
        g_sessionKey = null;
        g_isReady = true;
        err = err || 412;
        _emit('not_logged_in');
        _save();
      } else if (err) {
        util.errorLog('UserStore.verifySessionKey: err:', err);
      } else {
        g_user = body;
        g_isReady = true;
        _emit('user_login');
      }
      done(err);
    });
  } else {
    g_user = null;
    g_currentOrgId = null;
    g_sessionKey = null;
    g_isReady = true;
    _emit('not_logged_in');
    _save();
    done('not_logged_in');
  }
}
function emailCode(params: any, done: (err: string | number) => void) {
  const { email } = params;
  const opts = {
    url: '/api/1/user/email_code',
    body: { email },
  };
  Api.post(opts, (err: any, body: any) => {
    if (err) {
      util.errorLog('UserStore.emailCode: err:', err, body);
    }
    done(err);
  });
}
function loginCode(
  params: any,
  done: (err: string | number, user?: any) => void
) {
  const { email, login_code } = params;
  const req_body: any = {
    email,
    login_code: login_code.replace(/[^0-9]/g, ''),
  };
  if (g_customDomain) {
    req_body.domain_prefix = g_customDomain;
  }
  const opts = {
    url: '/api/1/user/email_login',
    body: req_body,
  };
  Api.post(opts, (err: any, body: any) => {
    if (err) {
      util.errorLog('UserStore.login: err:', err, body);
      done(err);
    } else {
      g_sessionKey = body.user_session_key;
      _save();
      verifySessionKey({}, done);
    }
  });
}
function logout(done: (err?: string) => void) {
  const opts = {
    url: '/api/1/user/logout',
  };
  post(opts, (err, body) => {
    if (err) {
      util.errorLog('UserStore.logout: err:', err, body);
    }
    g_sessionKey = null;
    g_user = null;
    _save();
    done(err);
    // allows logout to nav before we enforce protected routes
    setTimeout(() => _emit('logout'), 0);
  });
}
function updateUser(params, done: (err?: any) => void) {
  const opts = {
    url: '/api/1/user/current',
    body: { user_name: params.user_name },
  };
  post(opts, (err, body) => {
    if (err) {
      util.errorLog('UserStore.updateUser: err:', err, body);
      done(err);
    } else {
      verifySessionKey({}, done);
    }
  });
}
function get(opts, done) {
  _request(opts, 'GET', done);
}
function post(opts, done) {
  _request(opts, 'POST', done);
}
function del(opts, done) {
  _request(opts, 'DELETE', done);
}
function _load(done) {
  asyncParallel(
    [
      (done) =>
        Storage.getItem(SESSION_KEY, (err, data) => {
          if (!err && data) {
            g_sessionKey = data;
          }
          done();
        }),
      (done) =>
        Storage.getItem(ORG_ID_KEY, (err, data) => {
          if (!err && data) {
            g_currentOrgId = data;
          }
          done();
        }),
    ],
    done
  );
}
function _save() {
  Storage.setItem({ key: SESSION_KEY, value: g_sessionKey });
  Storage.setItem({ key: ORG_ID_KEY, value: g_currentOrgId });
}
function _request(opts, method, done) {
  if (typeof opts === 'string') {
    opts = {
      url: opts,
    };
  }
  opts.method = method;
  if (!opts.query) {
    opts.query = {};
  }
  opts.query.user_session_key = g_sessionKey;
  Api.request(opts, done);
}
